<template>
    <div
        :class="['slice-testimonials--default__card', {'slice-testimonials--default__card--is-video-playing': videoUnveiled}]"
        :data-testid="`testimonials-card-${index}`"
    >
        <div class="slice-testimonials--default__card-image" :data-testid="`testimonials-card-${index}-illustration`">
            <PrismicImage
                v-if="isFilled.image(item.testimonialIllustration)"
                :field="item.testimonialIllustration"
                :sizes="`(max-width: 767px) 85vw, 394px`"
                :alt="`Testimonial from ${item.intervieweeName}, ${item.intervieweeJobTitle}`"
            />

            <MktVideoPlayer
                v-if="isFilled.embed(item.testimonialVideo)"
                ref="videoPlayer"
                :src="videoSrc"
                class="slice-testimonials--default__card-video"
            />
            <div
                v-if="!videoUnveiled && isFilled.image(item.testimonialsVideoCover)"
                ref="coverRef"
                class="slice-testimonials--default__card-video-cover"
                :data-testid="`testimonials-card-${index}-video-cover`"
                @click="startVideo()"
            >
                <PrismicImage :field="item.testimonialsVideoCover" class="slice-testimonials--default__card-video-cover-image" />
                <div class="slice-testimonials--default__card-video-cover-overlay" />
                <div class="slice-testimonials--default__card-video-cover-icon">
                    <VJoyIcon name="play" size="large" color="white" />
                </div>
            </div>

            <div
                v-if="isFilled.image(item.testimonialLogo)"
                class="slice-testimonials--default__card-logo"
                :data-testid="`testimonials-card-${index}-logo`"
            >
                <PrismicImage v-if="isFilled.image(item.testimonialLogo)" :field="item.testimonialLogo" :alt="item.testimonialLogo.alt ?? ''" />
            </div>
        </div>
        <div class="slice-testimonials--default__card-body">
            <PrismicRichText
                v-if="isFilled.richText(item.testimonialDescription)"
                :field="item.testimonialDescription"
                class="slice-testimonials--default__card-description"
                :data-testid="`testimonials-card-${index}-description`"
            />
            <div class="slice-testimonials--default__card-footer">
                <div class="slice-testimonials--default__card-interviewee-info">
                    <div
                        v-if="isFilled.keyText(item.intervieweeName)"
                        class="slice-testimonials--default__card-interviewee-name"
                        :data-testid="`testimonials-card-${index}-interviewee-name`"
                    >
                        {{ item.intervieweeName }}
                    </div>
                    <div
                        v-if="isFilled.keyText(item.intervieweeJobTitle)"
                        class="slice-testimonials--default__card-interviewee-job"
                        :data-testid="`testimonials-card-${index}-interviewee-job`"
                    >
                        {{ item.intervieweeJobTitle }}
                    </div>
                </div>

                <MuiLink
                    v-if="isFilled.link(item.link) && item.link.url && item.link.text"
                    :href="$withLocalePrefix(item.link.url)"
                    :text="item.link.text"
                    :target="(item.link as FilledLinkToWebField).target || undefined"
                    class="slice-testimonials--default__link"
                    data-component-source="testimonials"
                    :data-testid="`testimonials-card-${index}-link`"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {computed, ref, useTemplateRef} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import type {FilledLinkToWebField} from '@prismicio/types';
    import {PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {TestimonialsSliceDefaultItem} from '@prismicio-types';
    import {MuiLink, MktVideoPlayer} from '#components';
    import {VJoyIcon} from '@maltjoy/core-vue';
    import {useVideoEmbedPlayer} from '../../../composables/useVideoEmbedPlayer';

    const props = defineProps<{
        item: TestimonialsSliceDefaultItem;
        index: number;
    }>();

    const {getEmbedSrc} = useVideoEmbedPlayer();

    const videoSrc = computed(() => {
        const src = getEmbedSrc(props.item.testimonialVideo);
        if (!src) return '';
        return src.includes('?') ? `${src}&rel=0` : `${src}?rel=0`;
    });

    const coverRef = useTemplateRef('coverRef');
    const videoUnveiled = ref(false);
    const videoPlayer = useTemplateRef('videoPlayer');

    function unveilCover() {
        if (coverRef.value) {
            coverRef.value.style.opacity = '0';
            coverRef.value.addEventListener('transitionend', () => {
                videoUnveiled.value = true;
            });
        }
    }

    function startVideo() {
        unveilCover();
        videoPlayer.value?.play();
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-testimonials--default__card {
        display: flex;
        flex-direction: column;
        width: 280px;
        max-width: 100%;
        height: 100%;
        background-color: var(--joy-color-background-default);
        border-radius: var(--joy-core-spacing-6);
        overflow: hidden;
        will-change: transform;
        opacity: 0.75;
        transition: opacity var(--joy-transition-duration-default) var(--joy-transition-timing-function);

        &--is-video-playing {
            opacity: 1 !important;
        }

        &:hover {
            box-shadow: var(--joy-core-elevation-1);
            opacity: 1;

            .slice-testimonials--default__card-image {
                > img,
                .slice-testimonials--default__card-video-cover-image {
                    transform: scale(1.03);
                }
            }
        }

        @include mq.screen_md(min) {
            width: 395px;
        }

        &-video,
        &-video-cover,
        &-image,
        &-logo {
            border-radius: var(--joy-core-radius-4) var(--joy-core-radius-4) 0 0;
        }

        &-image {
            position: relative;
            max-height: 225px;
            min-height: 225px;

            @include mq.screen_md(min) {
                max-height: 211px;
            }

            img {
                transition: transform var(--joy-transition-duration-default) var(--joy-transition-timing-function);
                display: block;
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        }

        &-play {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            pointer-events: none;
        }

        &-video-cover {
            transition: opacity var(--joy-transition-duration-default) var(--joy-transition-timing-function);
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            overflow: hidden;

            &-image,
            &-overlay {
                position: absolute;
                width: 100%;
                height: 100%;
                object-fit: cover;
            }

            &-overlay {
                opacity: 0.3;
                background: var(--joy-color-neutral-40);
            }

            &-icon {
                z-index: 1;
            }
        }

        &-logo {
            position: absolute;
            bottom: -25px;
            left: 0;
            z-index: 1;
            padding: var(--joy-core-spacing-3) var(--joy-core-spacing-5);
            background-color: var(--joy-color-background-default);
            max-height: 54px;
            min-width: 150px;

            img {
                display: block;
                height: 24px;
                max-width: 150px;
                object-fit: contain;
            }
        }

        &-body {
            display: flex;
            flex: 1;
            flex-direction: column;
            gap: var(--joy-core-spacing-4);
            padding: var(--joy-core-spacing-7) var(--joy-core-spacing-4);
        }

        &-footer {
            display: flex;
            flex-direction: column;
            justify-content: flex-end;
            gap: var(--joy-core-spacing-4);
        }

        &-description,
        &-interviewee-job {
            @include text.getFontBySize(primary-400);

            p {
                margin: 0;
            }
        }

        &-interviewee-info {
            display: flex;
            flex-direction: column;
            margin-top: auto;
        }

        &-interviewee-name {
            @include text.getFontBySize(primary-600);

            font-weight: var(--joy-font-weight-bold);
            color: var(--joy-color-neutral-60);
        }

        &-interviewee-job {
            color: var(--joy-color-neutral-40);
        }
    }
</style>
